import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import "../styles/Inter.css";

interface Opportunity {
  id: string | number;
  type: string;
  qte: number;
  pu: number;
  ttc: number;
  date?: Timestamp | Date | string | number; // Accepter différents formats de date
  createdAt?: string | Timestamp | Date; // Champ alternatif pour la date de création
  amount?: number;
  amineAmount?: number;
}

interface FactureFoodinnovModalProps {
  show: boolean;
  handleClose: () => void;
  initialData: any;
  preselectedDevis?: any | null; // Devis pré-sélectionné depuis la navigation
}

const FactureFoodinnovModal: React.FC<FactureFoodinnovModalProps> = ({
  show,
  handleClose,
  initialData,
  preselectedDevis,
}) => {
  const [formData, setFormData] = useState({
    companyId: "",
    opportunityType: "",
    qte: 1,
    pu: 0,
    montantHT: 0,
    montantTTC: 0,
    amineAmount: 0,
    amineAmountTTC: 0,
    invoiceNumber: "",
    pourcentage: 0,
    isSigned: false,
    creationDate: new Date().toISOString().split('T')[0],
    status: "En attente de Paiement",
    tva: 20,
  });

  const [tvaIsCustom, setTvaIsCustom] = useState(false);

  const [companies, setCompanies] = useState<string[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [selectedOpportunityInModal, setSelectedOpportunityInModal] = useState<Opportunity | null>(null);
  const [selectedOpportunityDetails, setSelectedOpportunityDetails] = useState<{
    quantite: number;
    prixUnitaire: number;
    montantHT: number;
    montantTTC: number;
  } | null>(null);

  // Référence pour suivre si la modale vient de s'ouvrir
  const prevShowRef = useRef(false);

  // Charger les sociétés au montage du composant
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const docRef = doc(db, "prospects", "foodinnovCompanies");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const companyNames = Object.entries(data)
            .filter(([_, companyData]) => companyData.devis || companyData.opportunities)
            .map(([name]) => name)
            .sort((a, b) => a.localeCompare(b));
          setCompanies(companyNames);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des sociétés:", error);
        toast.error("Erreur lors de la récupération des sociétés");
      }
    };

    fetchCompanies();
  }, []);

  // Pré-sélectionner le devis si il est passé en prop
  useEffect(() => {
    if (show && preselectedDevis && !initialData) {
      // Pré-remplir la société
      setFormData(prev => ({
        ...prev,
        companyId: preselectedDevis.companyId || "",
      }));
      // Les opportunités seront chargées dans l'useEffect suivant
    }
  }, [show, preselectedDevis, initialData]);

  // Gérer les données initiales et charger les opportunités
  useEffect(() => {
    // Ne réinitialiser que lorsque la modale s'ouvre (show passe de false à true)
    const isOpening = show && !prevShowRef.current;
    prevShowRef.current = show;

    if (!show) {
      return; // Ne rien faire si la modale est fermée
    }

    if (initialData && isOpening) {
      console.log("Initial Data:", initialData);
      setFormData({
        companyId: initialData.companyId || "",
        opportunityType: initialData.opportunityType || "",
        qte: initialData.qte || 1,
        pu: initialData.pu || 0,
        montantHT: initialData.montantHT || 0,
        montantTTC: initialData.montantTTC || 0,
        amineAmount: initialData.amineAmount || 0,
        amineAmountTTC: initialData.amineAmountTTC || 0,
        invoiceNumber: initialData.invoiceNumber || "",
        pourcentage: initialData.pourcentage || 0,
        isSigned: initialData.isSigned || false,
        creationDate: initialData.date ? new Date(initialData.date.toDate()).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        status: initialData.status || "En attente de Paiement",
        tva: initialData.tva !== undefined ? initialData.tva : 20,
      });
      const tvaValue = initialData.tva !== undefined ? initialData.tva : 20;
      setTvaIsCustom(tvaValue !== 20);

      // Charger les opportunités de la société sélectionnée
      const fetchOpportunities = async () => {
        try {
          const docRef = doc(db, "prospects", "foodinnovCompanies");
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            const companyData = data[initialData.companyId];
            if (companyData?.opportunities && Array.isArray(companyData.opportunities)) {
              const relevantOpportunities = companyData.opportunities;
              setOpportunities(relevantOpportunities);

              // Trouver l'index de l'opportunité correspondante
              const opportunityIndex = relevantOpportunities.findIndex(
                (opp: Opportunity) => opp.type === initialData.opportunityType
              );

              console.log("=== RECHERCHE D'OPPORTUNITÉ ===");
              console.log("Opportunités disponibles:", relevantOpportunities);
              console.log("Opportunité recherchée:", initialData.opportunityType);
              console.log("Index trouvé:", opportunityIndex);

              // Recherche plus précise : trouver l'opportunité qui correspond le mieux aux données de la facture
              let bestMatchIndex = -1;
              let bestMatchScore = 0;

              relevantOpportunities.forEach((opp: Opportunity, index: number) => {
                if (opp.type === initialData.opportunityType) {
                  let score = 0;
                  
                  // Vérifier la correspondance de la quantité
                  if (Math.abs(Number(opp.qte) - Number(initialData.qte)) < 0.01) {
                    score += 3; // Correspondance exacte de quantité
                  }
                  
                  // Vérifier la correspondance du prix unitaire
                  if (Math.abs(Number(opp.pu) - Number(initialData.pu)) < 0.01) {
                    score += 3; // Correspondance exacte de prix
                  }
                  
                  // Vérifier la correspondance du montant HT
                  const oppMontantHT = Number(opp.amount || (opp.qte * opp.pu));
                  if (Math.abs(oppMontantHT - Number(initialData.montantHT)) < 0.01) {
                    score += 2; // Correspondance exacte de montant HT
                  }
                  
                  // Vérifier la correspondance du montant TTC
                  if (Math.abs(Number(opp.ttc) - Number(initialData.montantTTC)) < 0.01) {
                    score += 2; // Correspondance exacte de montant TTC
                  }
                  
                  console.log(`Opportunité ${index}: ${opp.type} - Score: ${score} (qte: ${opp.qte}, pu: ${opp.pu}, HT: ${oppMontantHT}, TTC: ${opp.ttc})`);
                  
                  if (score > bestMatchScore) {
                    bestMatchScore = score;
                    bestMatchIndex = index;
                  }
                }
              });

              console.log("Meilleure correspondance trouvée:", bestMatchIndex, "avec score:", bestMatchScore);

              // Utiliser la meilleure correspondance trouvée, sinon utiliser la première correspondance par type
              const finalIndex = bestMatchIndex !== -1 ? bestMatchIndex : opportunityIndex;

              if (finalIndex !== -1) {
                const matchingOpp = relevantOpportunities[finalIndex];
                console.log("Opportunité correspondante trouvée:", matchingOpp);
                
                setSelectedOpportunityInModal(matchingOpp);
                
                // En mode édition, utiliser les données EXACTES de la facture, pas celles de l'opportunité
                setSelectedOpportunityDetails({
                  quantite: Number(initialData.qte) || 1,
                  prixUnitaire: Number(initialData.pu) || 0,
                  montantHT: Number(initialData.montantHT) || 0,
                  montantTTC: Number(initialData.montantTTC) || 0,
                });
                
                // Mettre à jour l'opportunityType avec l'index pour le select
                setFormData(prev => ({
                  ...prev,
                  opportunityType: finalIndex.toString(),
                  // Garder les données exactes de la facture
                  qte: Number(initialData.qte) || 1,
                  pu: Number(initialData.pu) || 0,
                  montantHT: Number(initialData.montantHT) || 0,
                  montantTTC: Number(initialData.montantTTC) || 0,
                  amineAmount: Number(initialData.amineAmount) || 0,
                  amineAmountTTC: Number(initialData.amineAmountTTC) || 0,
                }));
                
                console.log("Opportunité sélectionnée avec succès, index:", finalIndex);
                console.log("Données de la facture utilisées:", {
                  qte: initialData.qte,
                  pu: initialData.pu,
                  montantHT: initialData.montantHT,
                  montantTTC: initialData.montantTTC
                });
              } else {
                // Si l'opportunité n'est pas trouvée, réinitialiser
                setSelectedOpportunityInModal(null);
                setSelectedOpportunityDetails(null);
                setFormData(prev => ({
                  ...prev,
                  opportunityType: "",
                  qte: 1,
                  pu: 0,
                  montantHT: 0,
                  montantTTC: 0,
                  amineAmount: 0,
                  amineAmountTTC: 0,
                }));
              }
            }
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des opportunités:", error);
          toast.error("Erreur lors de la récupération des opportunités");
        }
      };

      if (initialData.companyId) {
        fetchOpportunities();
      }
    } else if (!initialData && isOpening) {
      // Mode ajout : tout réinitialiser seulement à l'ouverture
      setFormData({
        companyId: "",
        opportunityType: "",
        qte: 1,
        pu: 0,
        montantHT: 0,
        montantTTC: 0,
        amineAmount: 0,
        amineAmountTTC: 0,
        invoiceNumber: "",
        pourcentage: 0,
        isSigned: false,
        creationDate: new Date().toISOString().split('T')[0],
        status: "En attente de Paiement",
        tva: 20,
      });
      setTvaIsCustom(false);
      setSelectedOpportunityInModal(null);
      setSelectedOpportunityDetails(null);
      setOpportunities([]);
    }
  }, [initialData, show]);

  // Charger les opportunités quand la société change
  useEffect(() => {
    const fetchOpportunities = async () => {
      if (!formData.companyId) {
        setOpportunities([]);
        setSelectedOpportunityInModal(null);
        setSelectedOpportunityDetails(null);
        // Réinitialiser l'opportunityType quand il n'y a pas de société sélectionnée
        setFormData(prev => ({
          ...prev,
          opportunityType: "",
          qte: 1,
          pu: 0,
          montantHT: 0,
          montantTTC: 0,
          amineAmount: 0,
          amineAmountTTC: 0,
        }));
        return;
      }

      try {
        const docRef = doc(db, "prospects", "foodinnovCompanies");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const companyData = data[formData.companyId];
          if (companyData?.opportunities && Array.isArray(companyData.opportunities)) {
            const relevantOpportunities = companyData.opportunities;
            setOpportunities(relevantOpportunities);
            
            // Si preselectedDevis est fourni, pré-remplir les données
            if (preselectedDevis && preselectedDevis.companyId === formData.companyId && !initialData) {
              // Chercher l'opportunité correspondante au devis
              const matchingOppIndex = relevantOpportunities.findIndex((opp: Opportunity) => {
                // Comparer par type d'opportunité
                const matchType = opp.type === preselectedDevis.opportunityType;
                
                // Normaliser les dates pour la comparaison
                const normalizeDate = (d: any) => {
                  if (!d) return null;
                  if (d instanceof Timestamp) return d.toDate().toISOString().slice(0, 10);
                  if (typeof d === 'string') return d.slice(0, 10);
                  if (d instanceof Date) return d.toISOString().slice(0, 10);
                  return null;
                };
                const matchDate = normalizeDate(opp.date) === normalizeDate(preselectedDevis.date);
                
                return matchType && matchDate;
              });
              
              if (matchingOppIndex >= 0) {
                const matchingOpp = relevantOpportunities[matchingOppIndex];
                setSelectedOpportunityInModal(matchingOpp);
                setSelectedOpportunityDetails({
                  quantite: Number(preselectedDevis.qte) || Number(matchingOpp.qte) || 1,
                  prixUnitaire: Number(preselectedDevis.pu) || Number(matchingOpp.pu) || 0,
                  montantHT: Number(preselectedDevis.amount) || Number(matchingOpp.amount) || 0,
                  montantTTC: Number(preselectedDevis.amount) * 1.2 || Number(matchingOpp.ttc) || 0,
                });
                
                // Pré-remplir les champs du formulaire
                const devisAmount = Number(preselectedDevis.amount) || 0;
                const devisDate = preselectedDevis.date instanceof Timestamp 
                  ? preselectedDevis.date.toDate().toISOString().split('T')[0]
                  : preselectedDevis.date instanceof Date
                  ? preselectedDevis.date.toISOString().split('T')[0]
                  : typeof preselectedDevis.date === 'string'
                  ? preselectedDevis.date.slice(0, 10)
                  : new Date().toISOString().split('T')[0];
                
                setFormData(prev => ({
                  ...prev,
                  opportunityType: matchingOppIndex.toString(),
                  qte: Number(preselectedDevis.qte) || Number(matchingOpp.qte) || 1,
                  pu: Number(preselectedDevis.pu) || Number(matchingOpp.pu) || 0,
                  montantHT: devisAmount,
                  montantTTC: devisAmount * 1.2,
                  creationDate: devisDate,
                }));
              } else {
                // Si l'opportunité n'est pas trouvée, créer une opportunité virtuelle
                const virtualOpp: Opportunity = {
                  id: preselectedDevis.id || '',
                  type: preselectedDevis.opportunityType || '',
                  qte: Number(preselectedDevis.qte) || 1,
                  pu: Number(preselectedDevis.pu) || 0,
                  ttc: Number(preselectedDevis.amount) * 1.2 || 0,
                  amount: Number(preselectedDevis.amount) || 0,
                  date: preselectedDevis.date,
                };
                
                setSelectedOpportunityInModal(virtualOpp);
                setSelectedOpportunityDetails({
                  quantite: Number(preselectedDevis.qte) || 1,
                  prixUnitaire: Number(preselectedDevis.pu) || 0,
                  montantHT: Number(preselectedDevis.amount) || 0,
                  montantTTC: Number(preselectedDevis.amount) * 1.2 || 0,
                });
                
                const devisDate = preselectedDevis.date instanceof Timestamp 
                  ? preselectedDevis.date.toDate().toISOString().split('T')[0]
                  : preselectedDevis.date instanceof Date
                  ? preselectedDevis.date.toISOString().split('T')[0]
                  : typeof preselectedDevis.date === 'string'
                  ? preselectedDevis.date.slice(0, 10)
                  : new Date().toISOString().split('T')[0];
                
                setFormData(prev => ({
                  ...prev,
                  opportunityType: "", // Pas d'index car opportunité virtuelle
                  qte: Number(preselectedDevis.qte) || 1,
                  pu: Number(preselectedDevis.pu) || 0,
                  montantHT: Number(preselectedDevis.amount) || 0,
                  montantTTC: Number(preselectedDevis.amount) * 1.2 || 0,
                  creationDate: devisDate,
                }));
              }
            }
            // Si en mode édition et que la société correspond, pré-sélectionner l'opportunité
            else if (initialData && initialData.companyId === formData.companyId) {
               // Recherche plus précise : trouver l'opportunité qui correspond le mieux aux données de la facture
               let bestMatchIndex = -1;
               let bestMatchScore = 0;

               relevantOpportunities.forEach((opp: Opportunity, index: number) => {
                 if (opp.type === initialData.opportunityType) {
                   let score = 0;
                   
                   // Vérifier la correspondance de la quantité
                   if (Math.abs(Number(opp.qte) - Number(initialData.qte)) < 0.01) {
                     score += 3; // Correspondance exacte de quantité
                   }
                   
                   // Vérifier la correspondance du prix unitaire
                   if (Math.abs(Number(opp.pu) - Number(initialData.pu)) < 0.01) {
                     score += 3; // Correspondance exacte de prix
                   }
                   
                   // Vérifier la correspondance du montant HT
                   const oppMontantHT = Number(opp.amount || (opp.qte * opp.pu));
                   if (Math.abs(oppMontantHT - Number(initialData.montantHT)) < 0.01) {
                     score += 2; // Correspondance exacte de montant HT
                   }
                   
                   // Vérifier la correspondance du montant TTC
                   if (Math.abs(Number(opp.ttc) - Number(initialData.montantTTC)) < 0.01) {
                     score += 2; // Correspondance exacte de montant TTC
                   }
                   
                   if (score > bestMatchScore) {
                     bestMatchScore = score;
                     bestMatchIndex = index;
                   }
                 }
               });

               // Utiliser la meilleure correspondance trouvée
               if (bestMatchIndex !== -1) {
                 const matchingOpp = relevantOpportunities[bestMatchIndex];
                 setSelectedOpportunityInModal(matchingOpp);
                 
                 // En mode édition, utiliser les données EXACTES de la facture, pas celles de l'opportunité
                 setSelectedOpportunityDetails({
                   quantite: Number(initialData.qte) || 1,
                   prixUnitaire: Number(initialData.pu) || 0,
                   montantHT: Number(initialData.montantHT) || 0,
                   montantTTC: Number(initialData.montantTTC) || 0,
                 });
                 
                 setFormData(prev => ({
                   ...prev,
                   opportunityType: bestMatchIndex.toString(),
                   // Garder les données exactes de la facture
                   qte: Number(initialData.qte) || 1,
                   pu: Number(initialData.pu) || 0,
                   montantHT: Number(initialData.montantHT) || 0,
                   montantTTC: Number(initialData.montantTTC) || 0,
                   amineAmount: Number(initialData.amineAmount) || 0,
                   amineAmountTTC: Number(initialData.amineAmountTTC) || 0,
                 }));
               } else {
                 // Si l'opportunité n'est pas trouvée, afficher quand même les données de la facture
                 setSelectedOpportunityInModal(null);
                 setSelectedOpportunityDetails({
                   quantite: Number(initialData.qte) || 1,
                   prixUnitaire: Number(initialData.pu) || 0,
                   montantHT: Number(initialData.montantHT) || 0,
                   montantTTC: Number(initialData.montantTTC) || 0,
                 });
               }
             } else {
              // Mode ajout ou société différente, réinitialiser
              setSelectedOpportunityInModal(null);
              setSelectedOpportunityDetails(null);
              setFormData(prev => ({
                ...prev,
                opportunityType: "",
                qte: 1,
                pu: 0,
                montantHT: 0,
                montantTTC: 0,
                amineAmount: 0,
                amineAmountTTC: 0,
              }));
            }
          } else {
            setOpportunities([]);
            setSelectedOpportunityInModal(null);
            setSelectedOpportunityDetails(null);
            setFormData(prev => ({
              ...prev,
              opportunityType: "",
              qte: 1,
              pu: 0,
              montantHT: 0,
              montantTTC: 0,
              amineAmount: 0,
              amineAmountTTC: 0,
            }));
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des opportunités:", error);
        toast.error("Erreur lors de la récupération des opportunités");
      }
    };

    fetchOpportunities();
  }, [formData.companyId, initialData]);

  // Fonction pour calculer le montant Amine
  const calculateAmineAmount = (montantHT: number, pourcentage: number) => {
    if (pourcentage === 0) {
      return montantHT;
    }
    return (montantHT * pourcentage) / 100;
  };

  // Fonction pour calculer le pourcentage à partir du montant Amine
  const calculatePourcentage = (montantHT: number, amineAmount: number) => {
    if (montantHT === 0) return 0;
    return (amineAmount * 100) / montantHT;
  };

  // Fonction pour calculer le montant TTC Amine
  const calculateAmineAmountTTC = (montantHTAmine: number, tvaRate: number = 20) => {
    return montantHTAmine * (1 + tvaRate / 100);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      
      // Si le pourcentage change, recalculer le montant Amine
      if (name === "pourcentage") {
        const pourcentageValue = parseFloat(value) || 0;
        const newAmineAmount = calculateAmineAmount(updated.montantHT, pourcentageValue);
        updated.amineAmount = newAmineAmount;
        updated.amineAmountTTC = calculateAmineAmountTTC(newAmineAmount, updated.tva);
      }
      // Si le montant Amine change, recalculer le pourcentage
      else if (name === "amineAmount") {
        const amineValue = parseFloat(value) || 0;
        const newPourcentage = calculatePourcentage(updated.montantHT, amineValue);
        updated.pourcentage = newPourcentage;
        updated.amineAmountTTC = calculateAmineAmountTTC(amineValue, updated.tva);
      }
      // Si la TVA change, recalculer uniquement le montant TTC RegExport
      else if (name === "tva") {
        const tvaValue = parseFloat(value) || 0;
        updated.tva = tvaValue;
        updated.amineAmountTTC = calculateAmineAmountTTC(updated.amineAmount, tvaValue);
      }
      // Si le statut de signature change, mettre à jour le statut
      else if (name === "isSigned") {
        updated.isSigned = e.target.checked;
      }
      
      return updated;
    });
  };

  const handleSelectChange = async (e: any) => {
    const { name, value } = e.target;
    
    if (name === "companyId") {
      try {
        const docRef = doc(db, "prospects", "foodinnovCompanies");
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const companyData = data[value];
          
          console.log("Company Data:", companyData);
          
          if (companyData?.opportunities && Array.isArray(companyData.opportunities)) {
            const relevantOpportunities = companyData.opportunities; 

            setOpportunities(relevantOpportunities || []);
            setSelectedOpportunityInModal(null);
            setSelectedOpportunityDetails(null);

            // Réinitialiser les champs liés à l'opportunité
            setFormData(prev => ({ 
              ...prev, 
              companyId: value, 
              opportunityType: "", 
              qte: 1, 
              pu: 0, 
              montantHT: 0, 
              montantTTC: 0, 
              amineAmount: 0, 
              amineAmountTTC: 0 
            }));
            
          } else {
            setOpportunities([]);
            setSelectedOpportunityInModal(null);
            setSelectedOpportunityDetails(null);
            setFormData(prev => ({ 
              ...prev, 
              companyId: value, 
              opportunityType: "", 
              qte: 1, 
              pu: 0, 
              montantHT: 0, 
              montantTTC: 0, 
              amineAmount: 0, 
              amineAmountTTC: 0 
            }));
          }
        } else {
          setOpportunities([]);
          setSelectedOpportunityInModal(null);
          setSelectedOpportunityDetails(null);
          setFormData(prev => ({ 
            ...prev, 
            companyId: value, 
            opportunityType: "", 
            qte: 1, 
            pu: 0, 
            montantHT: 0, 
            montantTTC: 0, 
            amineAmount: 0, 
            amineAmountTTC: 0 
          }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données de la société:", error);
        toast.error("Erreur lors de la récupération des données de la société");
      }
    } else if (name === "opportunityType") {
      // Utiliser l'index pour trouver l'opportunité exacte
      const opportunityIndex = parseInt(value);
      
      if (value === "" || isNaN(opportunityIndex)) {
        // Aucune opportunité sélectionnée
        setSelectedOpportunityInModal(null);
        setSelectedOpportunityDetails(null);
        setFormData(prev => ({ 
          ...prev, 
          opportunityType: "", 
          qte: 1, 
          pu: 0, 
          montantHT: 0, 
          montantTTC: 0, 
          amineAmount: 0,
          amineAmountTTC: 0 
        }));
      } else {
        const opp = opportunities[opportunityIndex];

        if (opp) {
          setSelectedOpportunityInModal(opp);
          const montantHT = Number(opp.amount || (opp.qte * opp.pu)) || 0;
          const amineAmount = calculateAmineAmount(montantHT, formData.pourcentage);
          const amineAmountTTC = calculateAmineAmountTTC(amineAmount, formData.tva);

          setFormData(prev => ({
            ...prev,
            opportunityType: opportunityIndex.toString(),
            qte: Number(opp.qte) || 1,
            pu: Number(opp.pu) || 0,
            montantHT: montantHT,
            montantTTC: Number(opp.ttc) || 0,
            amineAmount: amineAmount,
            amineAmountTTC: amineAmountTTC,
          }));
          
          setSelectedOpportunityDetails({
            quantite: Number(opp.qte) || 1,
            prixUnitaire: Number(opp.pu) || 0,
            montantHT: montantHT,
            montantTTC: Number(opp.ttc) || 0,
          });
        } else {
          setSelectedOpportunityInModal(null);
          setSelectedOpportunityDetails(null);
          setFormData(prev => ({ 
            ...prev, 
            opportunityType: "", 
            qte: 1, 
            pu: 0, 
            montantHT: 0, 
            montantTTC: 0, 
            amineAmount: 0,
            amineAmountTTC: 0 
          }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!initialData && !selectedOpportunityInModal) {
      toast.error("Veuillez sélectionner une opportunité.");
      return;
    }

    try {
      const docRef = doc(db, "prospects", "foodinnovCompanies");
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        toast.error("Document non trouvé");
        return;
      }

      const data = docSnap.data();
      const companyData = data[formData.companyId] || { invoices: [] };

      // Récupérer le type d'opportunité depuis l'opportunité sélectionnée
      const opportunityType = selectedOpportunityInModal ? selectedOpportunityInModal.type : formData.opportunityType;

      // Convertir la date du formulaire en Timestamp
      // Utiliser l'heure locale pour éviter les problèmes de fuseau horaire
      let creationDate: Timestamp;
      if (formData.creationDate) {
        // Créer la date avec l'heure locale à minuit pour éviter les problèmes de fuseau horaire
        const dateParts = formData.creationDate.split('-');
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // Les mois sont 0-indexés en JavaScript
        const day = parseInt(dateParts[2]);
        const localDate = new Date(year, month, day, 12, 0, 0); // Utiliser midi pour éviter les problèmes de fuseau horaire
        creationDate = Timestamp.fromDate(localDate);
        console.log("=== CONVERSION DATE ===");
        console.log("Date saisie dans le formulaire:", formData.creationDate);
        console.log("Date convertie (année, mois, jour):", year, month + 1, day);
        console.log("Date locale créée:", localDate);
        console.log("Timestamp créé:", creationDate);
        console.log("Date du Timestamp:", creationDate.toDate().toLocaleDateString("fr-FR"));
      } else {
        creationDate = initialData?.date || Timestamp.now();
        console.log("=== DATE PAR DÉFAUT ===");
        console.log("Utilisation de la date initiale ou maintenant:", creationDate.toDate().toLocaleDateString("fr-FR"));
      }

      // Uid toujours présent : préservé en édition, généré à la création
      const invoiceUid = initialData?.uid || (Date.now().toString() + Math.random().toString(36).substring(2, 8));

      const newInvoice = {
        id: initialData ? initialData.id : (formData.invoiceNumber || ""),
        companyId: formData.companyId,
        opportunityType: opportunityType,
        qte: formData.qte,
        pu: formData.pu,
        montantHT: formData.montantHT,
        montantTTC: formData.montantTTC,
        amineAmount: formData.amineAmount,
        amineAmountTTC: formData.amineAmountTTC,
        pourcentage: formData.pourcentage,
        date: creationDate,
        updatedAt: Timestamp.now(),
        isSigned: formData.isSigned,
        status: formData.status,
        tva: formData.tva,
        uid: invoiceUid,
        ...(formData.invoiceNumber ? { invoiceNumber: formData.invoiceNumber } : {}),
      };

      let updatedInvoices;
      if (initialData) {
        // Remplacement en place via map — évite tout doublon
        updatedInvoices = companyData.invoices.map((inv: any) => {
          const matchByUid = initialData.uid && inv.uid && inv.uid === initialData.uid;
          const matchByDate = !matchByUid && inv.date?.toMillis?.() === initialData.date?.toMillis?.();
          return (matchByUid || matchByDate) ? newInvoice : inv;
        });

        const wasUpdated = updatedInvoices.some((inv: any) => inv === newInvoice);
        if (!wasUpdated) {
          console.error("ERREUR : Facture non trouvée en mode édition");
          toast.error("Erreur : Impossible de trouver la facture à modifier. Veuillez réessayer.");
          return;
        }
      } else {
        // Ajout d'une nouvelle facture
        updatedInvoices = [...(companyData.invoices || []), newInvoice];
      }

      await updateDoc(docRef, {
        [`${formData.companyId}.invoices`]: updatedInvoices,
      });

      toast.success(initialData ? "Facture modifiée avec succès" : "Facture ajoutée avec succès");
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de la facture:", error);
      toast.error("Erreur lors de la sauvegarde de la facture");
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton className="inter-font">
        <Modal.Title>
          {initialData ? "Modifier la facture" : "Ajouter une facture"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit} className="inter-font">
          <Form.Group className="mb-3">
            <Form.Label>Numéro de facture</Form.Label>
            <Form.Control
              type="text"
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={handleInputChange}
              placeholder="Laissez vide si pas de numéro de facture"
              className="inter-font"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Date de création</Form.Label>
            <Form.Control
              type="date"
              name="creationDate"
              value={formData.creationDate}
              onChange={handleInputChange}
              className="inter-font"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Société</Form.Label>
            <Form.Select
              name="companyId"
              value={formData.companyId}
              onChange={handleSelectChange}
              required
              className="inter-font"
            >
              <option value="">Sélectionner une société</option>
              {companies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Opportunité</Form.Label>
            <Form.Select
              name="opportunityType"
              value={formData.opportunityType}
              onChange={handleSelectChange}
              required={true}
              className="inter-font"
              disabled={opportunities.length === 0}
            >
              <option value="">Sélectionner une opportunité</option>
              {opportunities.map((opp, idx) => {
                const displayType = opp.type && opp.type.trim() !== '' ? opp.type : 'Opportunité sans nom';
                
                // Formater la date
                let dateStr = "";
                if (opp.date) {
                  const date = opp.date;
                  // Vérifier si c'est un Timestamp Firestore
                  if (date && typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') {
                    dateStr = (date as any).toDate().toLocaleDateString("fr-FR");
                  }
                  // Si c'est une Date JavaScript
                  else if (date instanceof Date) {
                    dateStr = date.toLocaleDateString("fr-FR");
                  }
                  // Si c'est une string ISO
                  else if (typeof date === 'string') {
                    try {
                      const dateObj = new Date(date);
                      if (!isNaN(dateObj.getTime())) {
                        dateStr = dateObj.toLocaleDateString("fr-FR");
                      }
                    } catch (e) {
                      console.warn("Erreur de formatage de date:", e);
                    }
                  }
                  // Si c'est un nombre (timestamp en millisecondes)
                  else if (typeof date === 'number') {
                    dateStr = new Date(date).toLocaleDateString("fr-FR");
                  }
                }
                // Essayer aussi avec createdAt si date n'existe pas
                if (!dateStr && opp.createdAt) {
                  try {
                    const createdAt = opp.createdAt;
                    if (typeof createdAt === 'string') {
                      const dateObj = new Date(createdAt);
                      if (!isNaN(dateObj.getTime())) {
                        dateStr = dateObj.toLocaleDateString("fr-FR");
                      }
                    } else if (createdAt && typeof createdAt === 'object' && 'toDate' in createdAt && typeof (createdAt as any).toDate === 'function') {
                      dateStr = (createdAt as any).toDate().toLocaleDateString("fr-FR");
                    } else if (createdAt instanceof Date) {
                      dateStr = createdAt.toLocaleDateString("fr-FR");
                    }
                  } catch (e) {
                    console.warn("Erreur de formatage createdAt:", e);
                  }
                }
                
                return (
                  <option key={`${opp.type}-${idx}`} value={idx.toString()}>
                    {dateStr ? dateStr + " - " : ""}{displayType} - {opp.qte} x {opp.pu}€ = {opp.ttc}€
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>

          {selectedOpportunityDetails && (
            <div className="mb-3 p-3" style={{ border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
              <h5 className="mb-3" style={{ color: '#333', fontWeight: '600' }}>Détails de l'opportunité sélectionnée</h5>
              <p style={{ margin: '0.5rem 0' }}>Quantité: {selectedOpportunityDetails.quantite}</p>
              <p style={{ margin: '0.5rem 0' }}>Prix unitaire: {selectedOpportunityDetails.prixUnitaire}€</p>
              <p style={{ margin: '0.5rem 0' }}>Montant HT: {selectedOpportunityDetails.montantHT}€</p>
              <p style={{ margin: '0.5rem 0' }}>Montant TTC: {selectedOpportunityDetails.montantTTC}€</p>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Pourcentage (%)</Form.Label>
            <Form.Control
              type="text"
              name="pourcentage"
              value={formData.pourcentage}
              onChange={handleInputChange}
              className="inter-font"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="TVA différente de 20%"
              checked={tvaIsCustom}
              onChange={(e) => {
                const checked = e.target.checked;
                setTvaIsCustom(checked);
                if (!checked) {
                  const newAmineAmountTTC = calculateAmineAmountTTC(formData.amineAmount, 20);
                  setFormData(prev => ({
                    ...prev,
                    tva: 20,
                    amineAmountTTC: newAmineAmountTTC,
                  }));
                }
              }}
              className="inter-font"
            />
            {tvaIsCustom && (
              <Form.Control
                type="number"
                name="tva"
                value={formData.tva}
                onChange={handleInputChange}
                min="0"
                max="100"
                step="0.1"
                placeholder="Ex: 0"
                className="inter-font mt-2"
              />
            )}
            {!tvaIsCustom && (
              <Form.Text className="text-muted">TVA appliquée : 20%</Form.Text>
            )}
          </Form.Group>

          <div className="d-flex gap-3">
            <Form.Group className="mb-3 flex-fill">
              <Form.Label>Montant HT à régler à RegExport (€)</Form.Label>
              <Form.Control
                type="text"
                name="amineAmount"
                value={formData.amineAmount}
                onChange={handleInputChange}
                className="inter-font"
              />
            </Form.Group>
            <Form.Group className="mb-3 flex-fill">
              <Form.Label>Montant TTC à régler à RegExport (€)</Form.Label>
              <Form.Control
                type="text"
                name="amineAmountTTC"
                value={formData.amineAmountTTC}
                onChange={handleInputChange}
                className="inter-font"
              />
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Statut</Form.Label>
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="inter-font"
            >
              <option value="En attente de Paiement">En attente de Paiement</option>
              <option value="Réglée">Réglée</option>
            </Form.Select>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose} className="inter-font">
              Annuler
            </Button>
            <Button variant="primary" type="submit" className="inter-font">
              {initialData ? "Modifier" : "Ajouter"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default FactureFoodinnovModal; 